import React, { useEffect, useState } from 'react';
import { Container, Form, Row, Col, Button, Card } from 'react-bootstrap';
import AlertOverlay from '../../../components/Alert';
import {
  getUiInterface,
  getPageName,
  getWordConnector,
} from '../../../locales/translationUtils';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa';
import NavBar, { NavBarItem } from '../../../components/NavBar';
import { useGeneric } from '../../../apis/generic/GenericProvider';

const CargoCreateView: React.FC = () => {
  const { t } = useTranslation();
  const { distinctOptions, fetchDistinctOptions } = useGeneric();
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

  const [alert, setAlert] = useState<{
    message: string;
    variant: 'success' | 'danger' | 'warning' | 'info';
  } | null>(null);

  const location = useLocation();
  const initialFormData = location.state?.formData

  interface Dimension {
    Quantity: number;
    Length: number;
    Width: number;
    High: number;
  }

  interface CargoFormData {
    FromId: string;
    ClientId: string;
    SupplierId: string;
    Pu: string;
    Dawb: string;
    ArrivalDate: string;
    Pieces: number;
    Dimensions: Dimension[];
    Dgr: boolean;
    DgrGoods: boolean;
    Weight: number;
    PoNumber: string;
    SoNumber: string;
    WrNumber: string;
    Description: string;
  }

const [formData, setFormData] = useState<CargoFormData>(
  initialFormData ? initialFormData : {
    FromId: '',
    ClientId: '',
    SupplierId: '',
    Pu: '',
    Dawb: '',
    ArrivalDate: '',
    Pieces: 0,
    Dimensions: [{ Quantity: 0, Length: 0, Width: 0, High: 0 }],
    Dgr: false,
    DgrGoods: false,
    Weight: 0,
    PoNumber: '',
    SoNumber: '',
    WrNumber: '',
    Description: '',
  }
);

  const navItems: NavBarItem[] = [
    { label: t('cargo.labels.home'), route: '/', iconName: 'house-door' },
    {
      label: t('cargo.labels.cargoList'),
      route: '/cargo-info',
      iconName: 'table',
    },
    {
      label: t('cargo.labels.supplierList'),
      route: '/cargo-info/',
      iconName: 'info-circle',
    },
  ];

  useEffect(() => {
    fetchDistinctOptions('wareHouse');
    fetchDistinctOptions('client');
    fetchDistinctOptions('supplier');
  }, []);

  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    const numericValue = Number(value);
    if (numericValue < 0) {
      return;
    }

    setFormData((prevState) => ({
      ...prevState,
      [name.charAt(0).toUpperCase() + name.slice(1)]:
        type === 'checkbox' ? checked : value,
    }));
  };

  const handleDimensionChange = (index: number, e: any) => {
    const { name, value } = e.target;

    const numericValue = Number(value);
    if (numericValue < 0) {
      return;
    }
    const updatedDimensions = [...formData.Dimensions];
    updatedDimensions[index] = {
      ...updatedDimensions[index],
      [name.charAt(0).toUpperCase() + name.slice(1)]: Number(value),
    };

    setFormData((prevState) => ({
      ...prevState,
      Dimensions: updatedDimensions,
    }));
  };

  const addDimension = () => {
    setFormData((prevState) => ({
      ...prevState,
      Dimensions: [
        ...prevState.Dimensions,
        { Quantity: 0, Length: 0, Width: 0, High: 0 },
      ],
    }));
  };

  const removeDimension = (index: number) => {
    const updatedDimensions = formData.Dimensions.filter((_, i) => i !== index);
    setFormData((prevState) => ({
      ...prevState,
      Dimensions: updatedDimensions,
    }));
  };

  const handleNextPage = () => {
    navigate('/cargo-info/create/step-two', { state: { formData } });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nullRequiredFields: string[] = [];

    if (!formData.FromId) nullRequiredFields.push('From');
    if (!formData.ClientId) nullRequiredFields.push('Client');
    if (!formData.SupplierId) nullRequiredFields.push('Fornecedor');
    if (!formData.Weight || Number(formData.Weight) == 0) nullRequiredFields.push('WGT');
    if (!formData.PoNumber || Number(formData.PoNumber) == 0) nullRequiredFields.push('poNumber');
    if (!formData.WrNumber || Number(formData.WrNumber) == 0) nullRequiredFields.push('wrNumber');

    if (nullRequiredFields.length > 0) {
      setInvalidFields(new Set(nullRequiredFields));
      const warnMessage: string =
        nullRequiredFields.length === 1
          ? getUiInterface('fieldRequiredSingle', {
              field: nullRequiredFields[0],
            })
          : getUiInterface('fieldRequiredMultiple', {
              fields: `${nullRequiredFields.slice(0, -1).join(', ')}${
                nullRequiredFields.length > 1
                  ? ` ${getWordConnector('and')} `
                  : ''
              }${nullRequiredFields.slice(-1)}`,
            });

      setAlert({ message: warnMessage, variant: 'danger' });
    } else {
      setInvalidFields(new Set()); // limpa erros
      handleNextPage();
      setAlert({ message: getUiInterface('formSent'), variant: 'success' });
    }
  };

  return (
    <Container fluid>
      <NavBar items={navItems} />
      <div className="mb-3"></div>
      {alert && (
        <AlertOverlay
          message={alert.message}
          variant={alert.variant}
          onClose={() => setAlert(null)}
        />
      )}

      <h2>{getPageName('createCargoInformation')}</h2>
      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="from">
              <Form.Label>{t('cargo.fields.from')}</Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  as="select"
                  name="fromId"
                  value={formData.FromId}
                  onChange={handleChange}
                  isInvalid={invalidFields.has('From')}
                  style={{
                    backgroundImage: 'none',
                  }}
                >
                  <option value="">{t('cargo.labels.selectOption')}</option>
                  {distinctOptions['wareHouse']?.map(
                    (option: { id: number; description: string }) => (
                      <option key={option.id} value={option.id.toString()}>
                        {option.description}
                      </option>
                    )
                  )}
                </Form.Control>
                <FaChevronDown
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="client">
              <Form.Label>{t('cargo.fields.client')}</Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  as="select"
                  name="clientId"
                  value={formData.ClientId}
                  onChange={handleChange}
                  isInvalid={invalidFields.has('Client')}
                  style={{
                    backgroundImage: 'none',
                  }}
                >
                  <option value="">{t('cargo.labels.selectOption')}</option>
                  {distinctOptions['client']?.map(
                    (option: { id: number; description: string }) => (
                      <option key={option.id} value={option.id.toString()}>
                        {option.description}
                      </option>
                    )
                  )}
                </Form.Control>
                <FaChevronDown
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="fornecedor">
              <Form.Label>{t('cargo.fields.supplier')}</Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  as="select"
                  name="supplierId"
                  value={formData.SupplierId}
                  onChange={handleChange}
                  isInvalid={invalidFields.has('Fornecedor')}
                  style={{
                    backgroundImage: 'none',
                  }}
                >
                  <option value="">{t('cargo.labels.selectOption')}</option>
                  {distinctOptions['supplier']?.map(
                    (option: { id: number; description: string }) => (
                      <option key={option.id} value={option.id.toString()}>
                        {option.description}
                      </option>
                    )
                  )}
                </Form.Control>
                <FaChevronDown
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="pu">
              <Form.Label>{t('cargo.fields.pu')}</Form.Label>
              <Form.Control
                type="date"
                name="pu"
                value={formData.Pu}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="dawb">
              <Form.Label>{t('cargo.fields.dawb')}</Form.Label>
              <Form.Control
                type="text"
                name="dawb"
                value={formData.Dawb}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="arrival">
              <Form.Label>{t('cargo.fields.arrival')}</Form.Label>
              <Form.Control
                type="date"
                name="arrivalDate"
                value={formData.ArrivalDate}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="quantityOfPieces">
              <Form.Label>{t('cargo.fields.quantityOfPieces')}</Form.Label>
              <Form.Control
                type="number"
                name="pieces"
                value={formData.Pieces}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <h5>{t('cargo.labels.dimensionsOfPieces')}</h5>
        <Card
          className="p-3 mb-3"
          style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #ced4da',
            borderRadius: '8px',
          }}
        >
          {formData.Dimensions.map((dimension, index) => (
            <Row key={index} className="mb-3">
              <Col md={2}>
                <Form.Group controlId={`quantity-${index}`}>
                  <Form.Label>{t('cargo.fields.quantityOfPieces')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="Quantity"
                    value={dimension.Quantity}
                    onChange={(e) => handleDimensionChange(index, e)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId={`length-${index}`}>
                  <Form.Label>{t('cargo.fields.length')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="Length"
                    value={dimension.Length}
                    onChange={(e) => handleDimensionChange(index, e)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId={`width-${index}`}>
                  <Form.Label>{t('cargo.fields.width')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="Width"
                    value={dimension.Width}
                    onChange={(e) => handleDimensionChange(index, e)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId={`high-${index}`}>
                  <Form.Label>{t('cargo.fields.high')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="High"
                    value={dimension.High}
                    onChange={(e) => handleDimensionChange(index, e)}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button
                  variant="danger"
                  onClick={() => removeDimension(index)}
                  disabled={formData.Dimensions.length === 1} // Desabilita o botão caso só tenha uma dimensão
                >
                  Remove
                </Button>
              </Col>
            </Row>
          ))}
          <Row className="mt-3">
            <Col>
              <Button variant="primary" onClick={addDimension}>
                Add Dimension
              </Button>
            </Col>
          </Row>
        </Card>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="dryIce">
              <Form.Check
                type="checkbox"
                label={t('cargo.fields.dryIce')}
                name="dgr"
                checked={formData.Dgr}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="dangerousGoods">
              <Form.Check
                type="checkbox"
                label={t('cargo.fields.dangerousGoods')}
                name="dgrGoods"
                checked={formData.DgrGoods}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="wgt">
              <Form.Label>{t('cargo.fields.wgt')}</Form.Label>
              <Form.Control
                type="number"
                name="weight"
                value={formData.Weight}
                onChange={handleChange}
                isInvalid={invalidFields.has('WGT')}
                style={{
                  backgroundImage: 'none'
                }}
              />
              <Form.Text
                className="text-muted"
                style={{ fontStyle: 'italic', fontSize: '0.85rem' }}
              >
                Exemplo: 10.089,001
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group controlId="poRo">
              <Form.Label>{t('cargo.fields.poRo')}</Form.Label>
              <Form.Control
                type="text"
                name="poNumber"
                value={formData.PoNumber}
                onChange={handleChange}
                isInvalid={invalidFields.has('poNumber')}
                style={{
                  backgroundImage: 'none'
                }}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="soNumber">
              <Form.Label>{t('cargo.fields.soNumber')}</Form.Label>
              <Form.Control
                type="text"
                name="soNumber"
                value={formData.SoNumber}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="wrNumber">
              <Form.Label>{t('cargo.fields.wrNumber')}</Form.Label>
              <Form.Control
                type="text"
                name="wrNumber"
                value={formData.WrNumber}
                onChange={handleChange}
                isInvalid={invalidFields.has('wrNumber')}
                style={{
                  backgroundImage: 'none'
                }}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={12}>
            <Form.Group controlId="remarks">
              <Form.Label>{t('cargo.fields.remarks')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.Description}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <Button
          className="mb-3"
          variant="primary"
          type="submit"
          onClick={handleSubmit}
        >
          {getUiInterface('Next')}
        </Button>
      </Form>
    </Container>
  );
};

export default CargoCreateView;
